/**
 * Mat template data
 * 20+ mat/passepartout definitions
 */

export interface MatData {
  id: string;
  name: string;
  color: string;
  category: MatCategory;
  texture?: 'smooth' | 'linen' | 'suede' | 'silk';
  isPremium?: boolean;
}

export type MatCategory = 'white' | 'neutral' | 'warm' | 'cool' | 'dark' | 'color';

/** Mat categories for the selector UI */
export const MAT_CATEGORIES: { id: MatCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'white', label: 'White' },
  { id: 'neutral', label: 'Neutral' },
  { id: 'warm', label: 'Warm' },
  { id: 'cool', label: 'Cool' },
  { id: 'dark', label: 'Dark' },
  { id: 'color', label: 'Color' },
];

/** All mat definitions */
export const MATS: MatData[] = [
  // === White family ===
  { id: 'mat-white', name: 'Bright White', color: '#ffffff', category: 'white' },
  { id: 'mat-snow', name: 'Snow White', color: '#fffafa', category: 'white' },
  { id: 'mat-antique-white', name: 'Antique White', color: '#faebd7', category: 'white' },
  { id: 'mat-ivory', name: 'Ivory', color: '#fffff0', category: 'white' },

  // === Neutral family ===
  { id: 'mat-cream', name: 'Cream', color: '#f5f5dc', category: 'neutral' },
  { id: 'mat-linen', name: 'Linen', color: '#faf0e6', category: 'neutral', texture: 'linen' },
  { id: 'mat-light-grey', name: 'Light Grey', color: '#d3d3d3', category: 'neutral' },
  { id: 'mat-grey', name: 'Grey', color: '#808080', category: 'neutral' },
  { id: 'mat-warm-grey', name: 'Warm Grey', color: '#a09890', category: 'neutral' },
  { id: 'mat-cool-grey', name: 'Cool Grey', color: '#8898a0', category: 'neutral' },

  // === Warm family ===
  { id: 'mat-sand', name: 'Sand', color: '#e0d0b8', category: 'warm' },
  { id: 'mat-butter', name: 'Butter Cream', color: '#f5e6c8', category: 'warm' },
  { id: 'mat-wheat', name: 'Wheat', color: '#f5deb3', category: 'warm' },
  { id: 'mat-parchment', name: 'Parchment', color: '#ece5d0', category: 'warm' },
  { id: 'mat-gold', name: 'Gold', color: '#daa520', category: 'warm', isPremium: true },

  // === Cool family ===
  { id: 'mat-ice-blue', name: 'Ice Blue', color: '#e0e8f0', category: 'cool' },
  { id: 'mat-silver', name: 'Silver', color: '#c0c0c0', category: 'cool' },
  { id: 'mat-dove', name: 'Dove Grey', color: '#b0b8c0', category: 'cool' },
  { id: 'mat-lavender', name: 'Lavender', color: '#e6e0f0', category: 'cool', isPremium: true },

  // === Dark family ===
  { id: 'mat-charcoal', name: 'Charcoal', color: '#36454f', category: 'dark' },
  { id: 'mat-black', name: 'Black', color: '#1a1a1a', category: 'dark' },
  { id: 'mat-dark-brown', name: 'Dark Brown', color: '#3b2820', category: 'dark' },

  // === Color family ===
  { id: 'mat-navy', name: 'Navy', color: '#000080', category: 'color', isPremium: true },
  { id: 'mat-burgundy', name: 'Burgundy', color: '#800020', category: 'color', isPremium: true },
  { id: 'mat-forest', name: 'Forest Green', color: '#228b22', category: 'color', isPremium: true },
  { id: 'mat-sage', name: 'Sage', color: '#b2ac88', category: 'color' },
  {
    id: 'mat-terracotta',
    name: 'Terracotta',
    color: '#cc6644',
    category: 'color',
    isPremium: true,
  },
  {
    id: 'mat-dusty-rose',
    name: 'Dusty Rose',
    color: '#c4a0a0',
    category: 'color',
    isPremium: true,
  },
  { id: 'mat-slate-blue', name: 'Slate Blue', color: '#6a7b8b', category: 'color' },
];

/** Get mat color by ID */
export function getMatColor(matId: string): string {
  return MATS.find((m) => m.id === matId)?.color ?? '#ffffff';
}

/** Get mat data by ID */
export function getMatData(matId: string): MatData | undefined {
  return MATS.find((m) => m.id === matId);
}
